import { useQuery } from '@apollo/client';
import { isNil } from 'lodash';
import { useMemo } from 'react';
import { Queries } from '@shared/api';
import { notNil } from '@shared/util/notNil';

export interface DmUser {
  id: number;
  fullName: string;
  username: string;
  imageUrl?: string | null;
  isMe: boolean;
}

interface DmChannelUsers {
  users: DmUser[];
  loading: boolean;
}

interface DmChannelMembersQuery {
  listChannelMemberships: Array<{ id: number; user: DmUser }>;
}

interface DmChannelMembersQueryVariables {
  input: { channelId: number };
}

/**
 * Fetches members for a single DM channel via listChannelMemberships.
 *
 * Callers should use `user.isMe` (e.g. `users.find(u => !u.isMe)` for the other participant in a 1:1 DM).
 */
export function useDmChannelUsers(channelId: number | undefined): DmChannelUsers {
  const { data, loading } = useQuery<DmChannelMembersQuery, DmChannelMembersQueryVariables>(
    Queries.channel.dmChannelMembers,
    {
      variables: { input: { channelId: channelId! } },
      skip: isNil(channelId),
    },
  );

  return useMemo(
    () => ({
      users: data?.listChannelMemberships?.map(m => m.user).filter(notNil) ?? [],
      loading,
    }),
    [data, loading],
  );
}
